import { useState, useEffect, useMemo, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Search, UserPlus, Users, UserCheck, CheckCircle, XCircle, RefreshCw } from "lucide-react"
import { Card, CardContent } from "components/ui/card"
import { Input } from "components/ui/input"
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "components/ui/sheet"
import { useResourceSubscription } from "contexts/NotificationsContext"
import { getLeads, updateLead } from "../services/leads"
import { Lead, STATUS_OPTIONS, getStatusBadge, formatDate } from "utils/leadHelpers"
import { LeadDetailPanel } from "./LeadDetailPanel"
import "./leads-responsive.css"

// Skeleton loader component
function LeadsListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 p-3 border-b animate-pulse"
          style={{ borderColor: "var(--hexa-border)" }}
        >
          <div className="flex-1 space-y-2">
            <div className="h-3 w-36 rounded" style={{ backgroundColor: "var(--hexa-border)" }} />
            <div className="h-2.5 w-24 rounded" style={{ backgroundColor: "var(--hexa-border)" }} />
          </div>
          <div className="h-5 w-16 rounded" style={{ backgroundColor: "var(--hexa-border)" }} />
          <div className="h-3 w-18 rounded" style={{ backgroundColor: "var(--hexa-border)" }} />
        </div>
      ))}
    </div>
  )
}

export function LeadsContent() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [activeStatusFilter, setActiveStatusFilter] = useState<string | null>(null)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const location = useLocation()

  // Apri il side-panel se si arriva dalla ricerca globale con openDetailId
  useEffect(() => {
    const state = location.state as { openDetailId?: number } | null
    if (state?.openDetailId && leads.length > 0) {
      const lead = leads.find((l) => l.id === state.openDetailId)
      if (lead) {
        setSelectedLead(lead)
        setSheetOpen(true)
      }
      window.history.replaceState({}, "")
    }
  }, [location.state, leads])

  // Funzione di fetch riutilizzabile
  const fetchLeadsData = useCallback(async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setIsLoading(true)
      }
      setIsRefreshing(true)
      setError(null)
      const response = await getLeads()
      const data = Array.isArray(response.data) ? response.data : []
      setLeads(data)
    } catch (err) {
      console.error("Error fetching leads:", err)
      setError("Errore nel caricamento dei leads")
      setLeads([])
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [])

  // Fetch leads on mount
  useEffect(() => {
    fetchLeadsData(true)
  }, [fetchLeadsData])

  // Realtime via WebSocket — sostituisce il polling fisso ogni 30s.
  // L'evento arriva quando un lead viene creato/aggiornato/cancellato server-side.
  useResourceSubscription('lead', () => fetchLeadsData(false))

  // Handler per refresh manuale
  const handleRefresh = () => {
    if (!isRefreshing) {
      fetchLeadsData(false)
    }
  }

  // Filter leads based on search and status
  const filteredLeads = useMemo(() => {
    let result = leads

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (lead) =>
          lead.full_name.toLowerCase().includes(query) ||
          lead.email.toLowerCase().includes(query) ||
          lead.company.toLowerCase().includes(query)
      )
    }

    // Status filter
    if (activeStatusFilter) {
      result = result.filter((lead) => lead.status === activeStatusFilter)
    }

    return result
  }, [leads, searchQuery, activeStatusFilter])

  // Calculate stats
  const stats = useMemo(() => {
    return {
      total: leads.length,
      new: leads.filter((l) => l.status === "new").length,
      contacted: leads.filter((l) => l.status === "contacted").length,
      converted: leads.filter((l) => l.status === "converted").length,
    }
  }, [leads])

  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead)
    setSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
  }

  const handleUpdateLead = async (leadId: number, updates: Partial<Lead>) => {
    try {
      const response = await updateLead(leadId, updates)
      // Update local state
      setLeads((prev) =>
        prev.map((lead) => (lead.id === leadId ? { ...lead, ...response.data } : lead))
      )
      // Update selected lead if it's the one being edited
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, ...response.data })
      }
    } catch (err) {
      console.error("Error updating lead:", err)
      throw err
    }
  }

  const toggleStatusFilter = (status: string) => {
    setActiveStatusFilter((prev) => (prev === status ? null : status))
  }

  const isEmpty = filteredLeads.length === 0
  const isEmptyDatabase = leads.length === 0

  return (
    <div className="leads-page flex flex-col min-w-0 h-full pb-24">
      {/* Main Content Area */}
      <div className="w-full min-w-0 max-w-[1280px] mx-auto px-3 sm:px-6 py-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
            <CardContent className="p-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(139, 92, 246, 0.15)" }}>
                  <Users className="w-4 h-4" style={{ color: "#8b5cf6" }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: "var(--hexa-text-primary)" }}>
                    {stats.total}
                  </p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                    Totali
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
            <CardContent className="p-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(139, 92, 246, 0.15)" }}>
                  <UserPlus className="w-4 h-4" style={{ color: "#8b5cf6" }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: "var(--hexa-text-primary)" }}>
                    {stats.new}
                  </p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                    Nuovi
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
            <CardContent className="p-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(245, 158, 11, 0.15)" }}>
                  <UserCheck className="w-4 h-4" style={{ color: "#f59e0b" }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: "var(--hexa-text-primary)" }}>
                    {stats.contacted}
                  </p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                    Contattati
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] hexa-card-shadow">
            <CardContent className="p-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(59, 130, 246, 0.15)" }}>
                  <CheckCircle className="w-4 h-4" style={{ color: "#3b82f6" }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: "var(--hexa-text-primary)" }}>
                    {stats.converted}
                  </p>
                  <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                    Convertiti
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-0 lg:max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "var(--hexa-text-muted)" }} />
            <Input
              placeholder="Cerca per nome, email o azienda..."
              aria-label="Cerca lead"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-10"
              style={{
                backgroundColor: "var(--hexa-surface)",
                borderColor: "var(--hexa-border)",
                color: "var(--hexa-text-primary)",
              }}
            />
          </div>

          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-lg transition-colors hover:bg-[var(--hexa-surface-elevated)] disabled:opacity-50"
            style={{
              backgroundColor: "var(--hexa-surface)",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "var(--hexa-border)",
            }}
            title="Aggiorna lista"
            aria-label="Aggiorna lista lead"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`}
              style={{ color: "var(--hexa-text-secondary)" }}
            />
          </button>

          {/* Status filter buttons */}
          <div className="leads-filters flex flex-wrap gap-2 w-full lg:w-auto" aria-label="Filtra per stato">
            {STATUS_OPTIONS.map((option) => {
              const isActive = activeStatusFilter === option.value
              return (
                <button
                  key={option.value}
                  onClick={() => toggleStatusFilter(option.value)}
                  aria-pressed={isActive}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: isActive ? option.bgColor : "var(--hexa-surface)",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isActive ? option.dotColor : "var(--hexa-border)",
                    color: "var(--hexa-text-primary)",
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: option.dotColor }}
                  />
                  {option.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Leads Table */}
        <Card className="bg-[var(--hexa-card-bg)] border-[var(--hexa-border)] overflow-hidden hexa-card-shadow">
          {isLoading ? (
            <LeadsListSkeleton count={5} />
          ) : error ? (
            <CardContent className="p-8 text-center">
              <XCircle className="w-9 h-9 mx-auto mb-3" style={{ color: "var(--hexa-text-muted)" }} />
              <h3 className="text-base font-semibold mb-1.5" style={{ color: "var(--hexa-text-primary)" }}>
                Errore
              </h3>
              <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                {error}
              </p>
            </CardContent>
          ) : isEmpty ? (
            <CardContent className="p-8 text-center">
              <UserPlus className="w-9 h-9 mx-auto mb-3" style={{ color: "var(--hexa-text-muted)" }} />
              <h3 className="text-base font-semibold mb-1.5" style={{ color: "var(--hexa-text-primary)" }}>
                {isEmptyDatabase ? "Nessun lead ricevuto" : "Nessun risultato"}
              </h3>
              <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                {isEmptyDatabase
                  ? "I lead arriveranno quando qualcuno compila il form sul tuo sito"
                  : "Prova a modificare i filtri di ricerca"}
              </p>
            </CardContent>
          ) : (
            <div className="min-w-0">
              <table className="leads-table w-full">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--hexa-border)" }}>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: "var(--hexa-text-secondary)" }}>
                      Nome / Email
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: "var(--hexa-text-secondary)" }}>
                      Azienda
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: "var(--hexa-text-secondary)" }}>
                      Status
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: "var(--hexa-text-secondary)" }}>
                      Sorgente
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: "var(--hexa-text-secondary)" }}>
                      Assegnato
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: "var(--hexa-text-secondary)" }}>
                      Data
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => {
                    const statusBadge = getStatusBadge(lead.status)
                    return (
                      <tr
                        key={lead.id}
                        onClick={() => handleLeadClick(lead)}
                        tabIndex={0}
                        aria-label={`Apri lead ${lead.full_name}`}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault()
                            handleLeadClick(lead)
                          }
                        }}
                        className="cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--hexa-border)" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--hexa-surface-elevated)"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent"
                        }}
                      >
                        <td className="leads-identity px-3 py-2.5">
                          <div>
                            <p className="font-medium" style={{ color: "var(--hexa-text-primary)" }}>
                              {lead.full_name}
                            </p>
                            <p className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                              {lead.email}
                            </p>
                          </div>
                        </td>
                        <td data-label="Azienda" className="px-3 py-2.5">
                          <span style={{ color: "var(--hexa-text-primary)" }}>
                            {lead.company || "-"}
                          </span>
                        </td>
                        <td data-label="Status" className="px-3 py-2.5">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap"
                            style={{
                              backgroundColor: statusBadge.bgColor,
                              color: "var(--hexa-text-primary)",
                            }}
                          >
                            <span
                              className="w-1 h-1 rounded-full"
                              style={{ backgroundColor: statusBadge.dotColor }}
                            />
                            {statusBadge.label}
                          </span>
                        </td>
                        <td data-label="Sorgente" className="px-3 py-2.5">
                          <span className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                            {lead.source || "-"}
                          </span>
                        </td>
                        <td data-label="Assegnato" className="px-3 py-2.5">
                          <span className="text-xs" style={{ color: "var(--hexa-text-secondary)" }}>
                            {lead.assigned_to_detail?.username || "-"}
                          </span>
                        </td>
                        <td data-label="Data" className="px-3 py-2.5">
                          <span className="text-xs" style={{ color: "var(--hexa-text-muted)" }}>
                            {formatDate(lead.created_at)}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Lead Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          className="leads-sheet w-full sm:max-w-[440px] h-[100dvh] gap-0 p-0 overflow-hidden"
          style={{
            backgroundColor: "var(--hexa-modal-bg)",
            borderColor: "var(--hexa-border)",
          }}
        >
          <SheetTitle className="sr-only">Dettaglio Lead</SheetTitle>
          <SheetDescription className="sr-only">Visualizza dettaglio lead</SheetDescription>
          {selectedLead && (
            <LeadDetailPanel
              lead={selectedLead}
              onClose={handleCloseSheet}
              onUpdate={handleUpdateLead}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
